// React
import { useEffect, useState } from "react"

// Device
import { isIOS, isMobileSafari } from "@/lib/device"

// UI
import { cn } from "@/lib/utils"
import { Button } from "@/components/Button"
import { CloudDownloadIcon } from "@/lib/icons"

// Translations
import { useTrans } from "@/i18n"

// Analytics
import { sendEvent } from "@/lib/analytics"

/**
 * Types
 *
 * DOCS: https://gist.github.com/rikukissa/cb291a4a82caa670d2e0547c520eae53
 */
interface IBeforeInstallPromptEvent extends Event {
	readonly platforms: string[]
	readonly userChoice: Promise<{
		outcome: "accepted" | "dismissed"
		platform: string
	}>
	prompt(): Promise<void>
}

// Add interface for window with standalone property
interface WindowWithStandalone extends Window {
	navigator: Navigator & {
		standalone?: boolean
	}
}

interface WebappInstallButtonProps {
	className?: string
}

/**
 * WebappInstallButton
 *
 * ALTERNATIVES:
 * https://github.com/pwa-builder/pwa-install
 * https://github.com/shnaveen25/react-pwa-installer-prompt
 * https://www.npmjs.com/package/react-pwa-install
 * https://github.com/docluv/add-to-homescreen
 *
 * @returns
 */
export function WebappInstallButton({
	className = "",
}: WebappInstallButtonProps) {
	const t = useTrans("common")

	// State
	const [showButton, setShowButton] = useState<boolean>(false)
	const [deferredPrompt, setDeferredPrompt] =
		useState<IBeforeInstallPromptEvent | null>(null)
	const [showIosInstructions, setShowIosInstructions] =
		useState<boolean>(false)

	// When PWA is installed
	function onPWAInstalled() {
		setShowButton(false)
		setDeferredPrompt(null)
		setShowIosInstructions(false)

		// Track event
		sendEvent("app", "install")
	}

	/**
	 * Check if app is in standalone mode (already installed)
	 * Works across all platforms and browsers
	 */
	function isInStandaloneMode(): boolean {
		// If we are in browser and window is available
		if (typeof window !== "undefined") {
			// iOS Safari
			if (isIOS() && window.navigator) {
				return (
					"standalone" in window.navigator &&
					(window as WindowWithStandalone).navigator.standalone ===
						true
				)
			}

			// Android and other platforms
			// Check if running in standalone mode (installed as PWA)
			if (window.matchMedia) {
				return (
					window.matchMedia("(display-mode: standalone)").matches ||
					window.matchMedia("(display-mode: fullscreen)").matches ||
					window.matchMedia("(display-mode: minimal-ui)").matches
				)
			}
		}

		// Not in standalone mode
		return false
	}

	// When mounted
	useEffect(() => {
		// When we are not in standalone mode and we are IOS, show button
		if (!isInStandaloneMode() && isIOS()) {
			setShowButton(true)
		}

		// This function is called when the install is available (ANDROID)
		const ready = (event: IBeforeInstallPromptEvent) => {
			// Prevent Chrome 67 and earlier from automatically showing the prompt
			event.preventDefault()

			// Stash the event so it can be triggered later.
			setDeferredPrompt(event as IBeforeInstallPromptEvent)

			// Show button when the install is available
			setShowButton(true)
		}

		/**
		 * Event: BeforeInstallPrompt
		 *
		 * NOTE: Non-standard: This feature is non-standard and is not on a standards track.
		 * Do not use it on production sites facing the Web: it will not work for every user.
		 * There may also be large incompatibilities between implementations and the behavior
		 * may change in the future.
		 * SOURCE: https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent/prompt#return_value
		 *
		 * COMPATIBILITY *
		 * NOT COMPATIBLE: Safari + Firefox
		 *
		 * On desktop:
		 * Chromium browsers support installing PWAs that have a manifest file on all supported desktop operating systems.
		 * Firefox and Safari do not support installing PWAs using a manifest file.
		 *
		 * On mobile:
		 * - On Android, Firefox, Chrome, Edge, Opera, and Samsung Internet Browser all support installing PWAs.
		 * - On iOS 16.3 and earlier, PWAs can only be installed with Safari.
		 * - On iOS 16.4 and later, PWAs can be installed from the Share menu in Safari, Chrome, Edge, Firefox, and Orion.
		 * - Chrome and Edge on iOS and iPadOS do not support PWA installation,
		 * 	 so the beforeinstallprompt event can't fire. In this case, the only
		 *   option is to open the PWA using Safari, where it is installable from the share,
		 *   add to the home screen menu.
		 *
		 * TRIGGER CRITERIA:
		 * In Chrome, your Progressive Web App must meet the following criteria before it will fire the beforeinstallprompt event and show the in-browser install promotion:
		 * - The web app is not already installed
		 * - The user needs to have clicked or tapped on the page at least once (at any time, even during a previous page load)
		 * - The user needs to have spent at least 30 seconds viewing the page (at any time)
		 * - Chrome release is NOT Beta/Dev/Canary,
		 *
		 * DOCS: https://web.dev/articles/install-criteria
		 * DOCS: https://developer.chrome.com/blog/app-install-banners-native
		 */
		window.addEventListener("beforeinstallprompt", ready as any)

		/**
		 * Detect when the PWA was successfully installed.
		 *
		 * NOTE: You can use the userChoice property
		 * to determine whether the user installed your app from within your user interface.
		 * But, if the user installs your PWA from the address bar or another browser component,
		 * userChoice won't help. Instead, you should listen for the appinstalled event, which
		 * fires whenever your PWA is installed, no matter what mechanism is used to install it.
		 */
		window.addEventListener("appinstalled", onPWAInstalled)

		// Remove event listener once unmounted
		return () => {
			window.removeEventListener("beforeinstallprompt", ready as any)
			window.removeEventListener("appinstalled", onPWAInstalled)
		}
	}, [])

	/**
	 * onClickInstallButton
	 * @returns
	 */
	function onClickInstallButton() {
		// Hide nittpm
		setShowButton(false)

		// Either use deferred prompt or show iOS instructions
		if (typeof deferredPrompt?.prompt === "function") {
			// Hide button
			setShowButton(false)

			// Show prompt
			void deferredPrompt.prompt()
		} else if (isMobileSafari() && isIOS() && !isInStandaloneMode()) {
			// Show IOS instructions only work on IOS safari
			setShowIosInstructions(true)
		}
	}

	// Render
	return (
		<>
			{/* Show install button */}
			{showButton === true ? (
				<div className={cn("flex items-center", className)}>
					<Button
						className="inline-block"
						onClick={() => onClickInstallButton()}
					>
						{t("common:app.banner.install")}
						<CloudDownloadIcon className="ml-1 w-4" />
					</Button>
				</div>
			) : null}

			{/* Show IOS instructions */}
			{showIosInstructions === true ? (
				<IOSInstructionsBanner
					setShowIosInstructions={setShowIosInstructions}
				/>
			) : null}
		</>
	)
}

/**
 * IOSInstructionsBanner
 *
 * @param param0
 * @returns
 */
function IOSInstructionsBanner({
	setShowIosInstructions,
}: {
	setShowIosInstructions: (show: boolean) => void
}) {
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Overlay */}
			<button
				type="button"
				className="fixed inset-0 bg-black/50"
				onClick={() => setShowIosInstructions(false)}
				aria-label="Close instructions"
			/>

			{/* Content */}
			<div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-semibold">Install on iOS</h3>
					<button
						onClick={() => setShowIosInstructions(false)}
						className="text-gray-400 hover:text-gray-500"
					>
						×
					</button>
				</div>
				<ol className="space-y-3 text-gray-600">
					<li className="flex items-start">
						<span className="mr-2 flex size-6 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-800">
							1
						</span>
						Open this website in Safari
					</li>
					<li className="flex items-start">
						<span className="mr-2 flex size-6 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-800">
							2
						</span>
						Tap the Share button{" "}
						<span className="inline-block">⎋</span>
					</li>
					<li className="flex items-start">
						<span className="mr-2 flex size-6 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-800">
							3
						</span>
						Scroll down and tap "Add to Home Screen"
					</li>
					<li className="flex items-start">
						<span className="mr-2 flex size-6 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-800">
							4
						</span>
						Tap "Add" in the top right
					</li>
				</ol>
			</div>
		</div>
	)
}
